// Task roulette's wheel on the next open (docs/roulette.md, flag `rouletteUI`). The server spins
// (server/roulette.ts); this module decides, once per page load, which spins this device has not
// shown yet and hands them to the shell one at a time (src/components/RouletteWheel.tsx), oldest
// first so the newest shows last. A spin is shown once per device: closing it adds its id to
// `op.rouletteSeen` (a JSON array of roulette ids). A spin whose task is already done or deleted is
// marked seen without showing.
//
// The plumbing is What's new's (src/whats-new.ts): the read starts once the app is signed in and the
// flag is on, the wheel shows only after the open board has loaded, the tour wins and the workshop
// holds it (src/app.tsx). The launch sheets run weekly scroll, What's new, then the wheel; while a
// wheel is due (the read still out, or spins still to show) the shell holds the recap and the prompts
// behind it: `rouletteHold()` / `useRoulette().hold`.
//
// The wheel before the task (owner, 2026-10-09: "When pressing a roulette notification, it navigates
// to the task, revealing the assignment before actually observing the roulette"): a task whose spin
// this device has not watched never shows its screen first. The shell asks `wheelBeforeTask` before
// it draws a task (useWheelBeforeTask); a spin to watch is held with the way to the task
// (`holdRouteForSpin`) and shows at once, whatever the board and ahead of the weekly scroll and What's
// new, and Got it (`closeSpin`) then opens the task. While this load's read is out the task waits for
// it, at most TASK_WAIT_MS. A roulette push reaching an open page reads the spins again (src/push.ts).
import { useEffect, useRef, useState } from "preact/hooks";
import {
  ROULETTE_SEEN_KEY,
  TASK_WAIT_MS,
  addSeen,
  parseSeen,
  spinForTask,
  spinsToShow,
  type RouletteResponse,
  type RouletteSpin,
} from "../shared/roulette.ts";
import type { Route } from "./router";

export { ROULETTE_SEEN_KEY };

/** Kept when storage refuses, so a spin is not shown twice in one open of a private window. */
let memory: string[] = [];

/** The ids this device has shown. */
export function seenRoulettes(): string[] {
  try {
    return parseSeen(localStorage.getItem(ROULETTE_SEEN_KEY));
  } catch {
    return memory;
  }
}

/** Marks `ids` as shown on this device. */
export function markRoulettesSeen(ids: readonly string[]): void {
  if (ids.length === 0) return;
  const next = addSeen(seenRoulettes(), ids);
  memory = next;
  try {
    localStorage.setItem(ROULETTE_SEEN_KEY, JSON.stringify(next));
  } catch {
    /* ignore: the copy in memory holds for this open */
  }
}

// ---------- this page load ----------

let started = false;
let loading = false;
/** A read has come back this page load (another may be out again). */
let readOnce = false;
/** Every spin the last read brought (the wheel before the task looks here). */
let known: RouletteSpin[] = [];
let queue: RouletteSpin[] = [];
let boardLoaded = false;
/** A spin shown now because its task was asked for, and the way to that task, taken by Got it. */
let heldRoute: { spin: RouletteSpin; route: () => void } | null = null;
/** A task asked for while the read is out: it waits for the read, at most TASK_WAIT_MS. */
let waiting: { taskId: string; route: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/**
 * The app is signed in and `rouletteUI` is on: read the household's spins once per page load and
 * queue the ones this device has not shown. A failed read shows nothing and holds nothing.
 */
export function startRoulette(load: () => Promise<RouletteResponse>): void {
  if (started) return;
  void readSpins(load);
}

/**
 * Reads the spins again, whatever this page load has read before, flag or no flag: the workshop's
 * "Spin the wheel now" (src/components/DevSheet.tsx) calls it after its spin so the new one, unseen
 * here, comes up at once (the board has loaded), and so does a roulette push reaching the open page
 * (src/push.ts). The latest read wins over one still out; it counts as this load's read, so
 * startRoulette does not read again.
 */
export function refreshRoulette(load: () => Promise<RouletteResponse>): Promise<void> {
  return readSpins(load);
}

let generation = 0;

function readSpins(load: () => Promise<RouletteResponse>): Promise<void> {
  const mine = ++generation;
  started = true;
  loading = true;
  emit();
  return load()
    .then((res) => {
      if (mine !== generation) return;
      known = res.spins ?? [];
      const { show, stale } = spinsToShow(known, seenRoulettes());
      markRoulettesSeen(stale);
      queue = show;
    })
    .catch(() => {
      if (mine === generation) queue = [];
    })
    .finally(() => {
      if (mine !== generation) return;
      loading = false;
      readOnce = true;
      // A task that waited for this read is decided now, before the shell hears of the read, so its
      // spin is already the one up when the wheel first draws.
      const asked = waiting;
      if (asked) {
        clearTimeout(asked.timer);
        waiting = null;
        decide(asked.taskId, asked.route);
      }
      emit();
    });
}

/** The open board has its list (src/views/Board.tsx): the wheel may show from now on. */
export function rouletteBoardLoaded(): void {
  if (boardLoaded) return;
  boardLoaded = true;
  emit();
}

/** Whether a wheel is due this open (the read still out, a spin still to show, or one held): the recap and the prompts wait. */
export function rouletteHold(): boolean {
  return heldRoute !== null || (started && (loading || queue.length > 0));
}

/** Whether the spin up now was asked for with its task (holdRouteForSpin): it goes ahead of the weekly scroll and What's new. */
export function rouletteHeld(): boolean {
  return heldRoute !== null;
}

/**
 * The spin to show now: a held one at once, whatever the board; else the first queued, once the board
 * has loaded and a read is back (a later read still out leaves the wheel up); else null.
 */
export function currentSpin(): RouletteSpin | null {
  if (heldRoute) return heldRoute.spin;
  return started && boardLoaded && readOnce ? (queue[0] ?? null) : null;
}

/** Whether the wheel shows now: a spin is up and nothing holds it (a tour, the first welcome, the workshop). */
export function rouletteShows(spin: RouletteSpin | null, held: boolean): boolean {
  return spin !== null && !held;
}

/** Got it (or Escape, once landed): mark it seen and move to the next; a held spin's task opens now, once. */
export function closeSpin(id: string): void {
  markRoulettesSeen([id]);
  queue = queue.filter((s) => s.id !== id);
  const route = heldRoute && heldRoute.spin.id === id ? heldRoute.route : null;
  if (route) heldRoute = null;
  emit();
  route?.();
}

// ---------- the wheel before the task ----------

/**
 * Shows `spin` now, whatever the board, ahead of the weekly scroll and What's new (the tour still wins
 * and the workshop still holds), and keeps `route` for Got it (closeSpin). One at a time: a newer
 * hold replaces it, and its spin waits in the queue as any unseen spin does.
 */
export function holdRouteForSpin(spin: RouletteSpin, route: () => void): void {
  heldRoute = { spin, route };
  emit();
}

function decide(taskId: string, route: () => void): void {
  const spin = spinForTask(known, seenRoulettes(), taskId);
  if (spin) holdRouteForSpin(spin, route);
  else route();
}

/**
 * The way to task `taskId` (`route` opens it), asked while `rouletteUI` is on (the caller checks the
 * flag). With this load's read back and none out: a spin this device has not shown for the task is
 * held with `route` and shows now, Got it then opening the task; with none, `route` runs at once.
 * With the read out (or not yet started): the task waits for it, at most `waitMs`, then goes on. A
 * newer request replaces one still waiting.
 */
export function wheelBeforeTask(taskId: string, route: () => void, waitMs: number = TASK_WAIT_MS): void {
  dropWaiting();
  if (readOnce && !loading) {
    decide(taskId, route);
    return;
  }
  const timer = setTimeout(() => {
    if (waiting?.timer !== timer) return;
    waiting = null;
    emit();
    route();
  }, waitMs);
  waiting = { taskId, route, timer };
  emit();
}

function dropWaiting(): boolean {
  if (!waiting) return false;
  clearTimeout(waiting.timer);
  waiting = null;
  return true;
}

/**
 * The address left task `taskId` before its gate opened (the browser's back, another link): whatever
 * waits or is held for it is dropped. The spin stays unseen in the queue, so it still shows once the
 * board has loaded; Got it then opens nothing.
 */
export function leaveTask(taskId: string): void {
  let changed = waiting?.taskId === taskId && dropWaiting();
  if (heldRoute && heldRoute.spin.task_id === taskId) {
    heldRoute = null;
    changed = true;
  }
  if (changed) emit();
}

/**
 * The gate on task `taskId` as it stands, read while the shell draws: "open" draws the task, "wait"
 * (the read is out, or not yet started) and "hold" (a spin to watch first) keep the screen under it.
 * Pure: wheelBeforeTask makes the request.
 */
export function taskGate(taskId: string): "open" | "wait" | "hold" {
  if (heldRoute && heldRoute.spin.task_id === taskId) return "hold";
  if (waiting?.taskId === taskId || !readOnce || loading) return "wait";
  return spinForTask(known, seenRoulettes(), taskId) ? "hold" : "open";
}

/** What the shell's gate remembers between draws: the screen under a task that waits, and the task let through. */
export interface GateMemory {
  under: Route;
  opened: string | null;
}

/**
 * One draw of the shell's gate (src/app.tsx useWheelBeforeTask; pure but for `memory`): the route to
 * draw for `address`, its task, and whether that task waits at the gate. `on`: `rouletteUI` is on;
 * `ready`: the shell draws screens (named and signed in). A task waits only on its arrival: one
 * already drawn stays drawn while the address names it (the flag turned on under it, the gate opened).
 * The screen under a waiting task is the last screen drawn that was not a task, the open board on a
 * fresh load.
 */
export function gateDraw(address: Route, on: boolean, ready: boolean, memory: GateMemory): { route: Route; taskId: string | null; gated: boolean } {
  const taskId = address.name === "task" ? address.id : null;
  if (memory.opened !== taskId) memory.opened = null;
  const gated = taskId !== null && on && ready && memory.opened !== taskId && taskGate(taskId) !== "open";
  if (!gated && ready) {
    if (taskId !== null) memory.opened = taskId;
    else memory.under = address;
  }
  return { route: gated ? memory.under : address, taskId, gated };
}

/**
 * The route the shell draws (src/app.tsx): the address's, except a task whose spin this device has
 * not watched. Then the screen under it stays (the open board on a load from a push or a link; on a
 * tap, the screen it came from) while the wheel plays, and Got it draws the task; while this load's
 * read is out the task waits for it, at most TASK_WAIT_MS. Leaving the address drops the wait.
 */
export function useWheelBeforeTask(address: Route, on: boolean, ready: boolean): Route {
  const [, redraw] = useState(0);
  const gate = useRef<GateMemory>({ under: { name: "open" }, opened: null }).current;
  useEffect(() => {
    const update = () => redraw((n) => n + 1);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);
  const { route, taskId, gated } = gateDraw(address, on, ready, gate);
  useEffect(() => {
    if (!gated || taskId === null) return;
    wheelBeforeTask(taskId, () => {
      gate.opened = taskId;
      redraw((n) => n + 1);
    });
    return () => leaveTask(taskId);
  }, [gated, taskId]);
  return route;
}

/** The shell's view: the spin to show now, whether the recap and the prompts must wait, and whether the spin was asked for with its task. */
export function useRoulette(): { spin: RouletteSpin | null; hold: boolean; held: boolean } {
  const read = () => ({ spin: currentSpin(), hold: rouletteHold(), held: rouletteHeld() });
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = () =>
      setState((prev) => {
        const next = read();
        return prev.spin === next.spin && prev.hold === next.hold && prev.held === next.held ? prev : next;
      });
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return state;
}

/** Tests only. */
export function resetRouletteForTests(): void {
  started = false;
  loading = false;
  readOnce = false;
  known = [];
  queue = [];
  boardLoaded = false;
  heldRoute = null;
  dropWaiting();
  memory = [];
  listeners.clear();
}
